import React, { createContext, useContext, useState, useCallback } from 'react';
import { authAPI, profileAPI } from '../profile/services';
import useIdleTimeout from '../hooks/useIdleTimeout';
import LogUtil from '../utils/LogUtil';
import { unregisterNativePush, autoRegisterOnLogin } from '../notification/nativePushService';
import { Capacitor } from '@capacitor/core';
import { secureStorage } from '../services/secureStorage';

const isNative = Capacitor.isNativePlatform();

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUT_MS = 15 * 60 * 1000;  // 15 minutes idle before logout
const SESSION_WARNING_MS = 2 * 60 * 1000;   // warn 2 minutes before logout

const AuthContext = createContext();

const setSessionCookie = () => {
  document.cookie = 'exsports_session=1; path=/; max-age=2592000; SameSite=Lax';
};
const clearSessionCookie = () => {
  document.cookie = 'exsports_session=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);

  // Show warning before session expires
  const handleSessionWarning = useCallback(() => {
    if (user) {
      setSessionWarning(true);
    }
  }, [user]);

  // Handle session timeout due to inactivity (fires after warning period if not extended)
  const handleIdleTimeout = useCallback(() => {
    if (user) {
      setSessionWarning(false);
      setSessionExpired(true);
      setUser(null);
      clearSessionCookie();
      secureStorage.removeItem('token');
      secureStorage.removeItem('socialUser');
      const currentPath = window.location.pathname + window.location.search;
      const authPaths = ['/signin', '/signup', '/claim-account', '/forgot-password'];
      const alreadyOnAuthPage = authPaths.some(p => currentPath.startsWith(p));
      if (!alreadyOnAuthPage) {
        localStorage.setItem('postLoginRedirect', currentPath);
        window.location.href = '/signin?expired=true';
      }
    }
  }, [user]);

  // Idle timeout disabled entirely (2026-07-03) — was forcing logouts during live
  // scoring: natural gaps between points plus a mobile screen lock meant users got
  // kicked out mid-game, with a real risk of losing the last few (debounced,
  // not-yet-saved) score updates. The JWT's own fixed expiry is the only session
  // limit now. Pass `false` here (not a removal) so this can be re-enabled by
  // flipping the condition back if a future use case needs it.
  const { resetTimer } = useIdleTimeout(handleIdleTimeout, handleSessionWarning, SESSION_TIMEOUT_MS, SESSION_WARNING_MS, false);

  // Extend session — called when user clicks "Stay logged in" in the warning dialog
  const extendSession = useCallback(() => {
    setSessionWarning(false);
    resetTimer();
  }, [resetTimer]);

  // Clear session expired flag
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
  }, []);

  const login = async (email, password, captchaAnswer) => {
    setIsLoading(true);
    try {
      // Login to ExportsAPI — returns user data + JWT token
      const apiResponse = await authAPI.login(email, password, captchaAnswer);

      if (apiResponse.token) {
        await secureStorage.setItem('token', apiResponse.token);
        await secureStorage.setItem('authMethod', 'email');
        await secureStorage.setItem('socialUser', JSON.stringify({
          id: apiResponse.id,
          email: apiResponse.email,
          firstName: apiResponse.firstName,
          lastName: apiResponse.lastName
        }));
        setSessionCookie();
      }

      const userData = {
        id: apiResponse.id,
        name: `${apiResponse.firstName} ${apiResponse.lastName}`,
        firstName: apiResponse.firstName,
        lastName: apiResponse.lastName,
        email: apiResponse.email,
        phoneNumber: apiResponse.phoneNumber,
        profileImage: apiResponse.profileImage || null,
        dob: apiResponse.dob || null,
        gender: apiResponse.gender || null,
        avatar: null,
        sport: 'Basketball',
        rating: 4.8,
        wins: 47,
        losses: 12,
        friends: 234,
        teammates: 18,
        totalPoints: 1247,
        rank: '#12 in City',
        address: apiResponse.address,
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,
        // KYN-499: resolved feature entitlements from the backend. Default to all-off
        // (empty object) so a missing/old response fails closed — feature stays hidden.
        featureFlags: apiResponse.featureFlags || {},
      };

      setUser(userData);
      LogUtil.setUserId(userData.id);
      autoRegisterOnLogin().catch(() => {});
      // Note: We only store token, not user data. User data is fetched fresh on each app load.
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const errorData = error.data || {};
      return {
        success: false,
        error: error.message || 'Login failed',
        code: errorData.code,
        requiresCaptcha: errorData.requiresCaptcha,
        captchaQuestion: errorData.captchaQuestion,
        attemptCount: errorData.attemptCount
      };
    } finally {
      setIsLoading(false);
    }
  };

  const facebookLogin = async (accessToken, authenticationToken, mode = 'signin') => {
    setIsLoading(true);
    try {
      const apiResponse = await authAPI.facebookLogin(accessToken, authenticationToken, mode);
      if (apiResponse.token) {
        await secureStorage.setItem('token', apiResponse.token);
        await secureStorage.setItem('authMethod', 'facebook');
        await secureStorage.setItem('socialUser', JSON.stringify({
          id: apiResponse.id,
          email: apiResponse.email,
          firstName: apiResponse.first_name,
          lastName: apiResponse.last_name,
        }));
        setSessionCookie();
      }
      const userData = {
        id: apiResponse.id,
        name: `${apiResponse.first_name} ${apiResponse.last_name}`.trim(),
        firstName: apiResponse.first_name,
        lastName: apiResponse.last_name,
        email: apiResponse.email,
        phoneNumber: apiResponse.phone_number,
        profileImage: apiResponse.profile_image || null,
        dob: apiResponse.dob || null,
        gender: apiResponse.gender || null,
        avatar: null,
        sport: 'Basketball',
        rating: 4.8, wins: 47, losses: 12, friends: 234,
        teammates: 18, totalPoints: 1247, rank: '#12 in City',
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,
      };
      setUser(userData);
      LogUtil.setUserId(userData.id);
      autoRegisterOnLogin().catch(() => {});
      return { success: true, linked: apiResponse.linked || false };
    } catch (error) {
      return { success: false, error: error.message || 'Facebook login failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (credential, mode = 'signin') => {
    setIsLoading(true);
    try {
      const apiResponse = await authAPI.googleLogin(credential, mode);
      if (!apiResponse.token) {
        throw new Error('Google Sign-In failed: no token returned from server');
      }
      await secureStorage.setItem('token', apiResponse.token);
      await secureStorage.setItem('authMethod', 'google');
      await secureStorage.setItem('socialUser', JSON.stringify({
        id: apiResponse.id,
        email: apiResponse.email,
        firstName: apiResponse.first_name,
        lastName: apiResponse.last_name,
      }));
      setSessionCookie();
      const userData = {
        id: apiResponse.id,
        name: `${apiResponse.first_name} ${apiResponse.last_name}`.trim(),
        firstName: apiResponse.first_name,
        lastName: apiResponse.last_name,
        email: apiResponse.email,
        phoneNumber: apiResponse.phone_number,
        profileImage: apiResponse.profile_image || null,
        dob: apiResponse.dob || null,
        gender: apiResponse.gender || null,
        avatar: null,
        sport: 'Basketball',
        rating: 4.8, wins: 47, losses: 12, friends: 234,
        teammates: 18, totalPoints: 1247, rank: '#12 in City',
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,
      };
      setUser(userData);
      LogUtil.setUserId(userData.id);
      autoRegisterOnLogin().catch(() => {});
      return { success: true, linked: apiResponse.linked || false };
    } catch (error) {
      return { success: false, error: error.message || 'Google login failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const appleLogin = async (identityToken, firstName, lastName) => {
    setIsLoading(true);
    try {
      const apiResponse = await authAPI.appleLogin(identityToken, firstName, lastName);
      if (apiResponse.token) {
        await secureStorage.setItem('token', apiResponse.token);
        await secureStorage.setItem('authMethod', 'apple');
        await secureStorage.setItem('socialUser', JSON.stringify({
          id: apiResponse.id,
          email: apiResponse.email,
          firstName: apiResponse.first_name,
          lastName: apiResponse.last_name,
        }));
        setSessionCookie();
      }
      const userData = {
        id: apiResponse.id,
        name: `${apiResponse.first_name || ''} ${apiResponse.last_name || ''}`.trim(),
        firstName: apiResponse.first_name,
        lastName: apiResponse.last_name,
        email: apiResponse.email,
        phoneNumber: apiResponse.phone_number,
        profileImage: apiResponse.profile_image || null,
        dob: apiResponse.dob || null,
        gender: apiResponse.gender || null,
        avatar: null,
        sport: 'Basketball',
        rating: 4.8, wins: 47, losses: 12, friends: 234,
        teammates: 18, totalPoints: 1247, rank: '#12 in City',
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,
      };
      setUser(userData);
      LogUtil.setUserId(userData.id);
      autoRegisterOnLogin().catch(() => {});
      return { success: true, linked: apiResponse.linked || false };
    } catch (error) {
      return { success: false, error: error.message || 'Apple Sign-In failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // Suppress 401 redirects for the entire logout sequence — any API call that
    // returns 401 during cleanup (e.g. DELETE /device-tokens with a stale token)
    // must NOT trigger window.location.href, which causes a full WebView reload
    // visible as an app crash on native.
    window.__loggingOut = true;
    try {
      await unregisterNativePush().catch(() => {});
      // Sign out of Google on native so the account picker shows on next Google Sign-In
      if (isNative) {
        const authMethod = await secureStorage.getItem('authMethod').catch(() => null);
        if (authMethod === 'google') {
          import('@codetrix-studio/capacitor-google-auth')
            .then(({ GoogleAuth }) => GoogleAuth.signOut())
            .catch(() => {});
        }
      }
      setUser(null);
      setSessionWarning(false);
      setSessionExpired(false);
      LogUtil.setUserId(null);
      clearSessionCookie();
      // Await storage removal so __loggingOut stays true until token is fully cleared.
      // Fire-and-forget would clear the flag before storage ops complete, letting any
      // in-flight API call see __loggingOut=false and trigger a redirect.
      await secureStorage.removeItem('token');
      await secureStorage.removeItem('socialUser');
      await secureStorage.removeItem('authMethod');
    } finally {
      window.__loggingOut = false;
    }
  };

  const signup = async (userData) => {
    setIsLoading(true);
    try {
      const apiResponse = await authAPI.register(userData);

      const newUser = {
        id: apiResponse.id,
        name: `${apiResponse.firstName} ${apiResponse.lastName}`,
        firstName: apiResponse.firstName,
        lastName: apiResponse.lastName,
        email: apiResponse.email,
        phoneNumber: apiResponse.phoneNumber,
        dateOfBirth: userData.dateOfBirth,
        gender: userData.gender,
        location: userData.location,
        skillLevel: userData.skillLevel,
        newsletterOptIn: userData.newsletterOptIn,
        avatar: null,
        sport: userData.sport || 'Basketball',
        rating: 0,
        wins: 0,
        losses: 0,
        friends: 0,
        teammates: 0,
        totalPoints: 0,
        rank: 'Unranked',
        joinedDate: apiResponse.createdAt || new Date().toISOString(),
        createdAt: apiResponse.createdAt,
        updatedAt: apiResponse.updatedAt,
        securityQuestion1: userData.securityQuestion1 || '',
        securityAnswer1: userData.securityAnswer1 || '',
        securityQuestion2: userData.securityQuestion2 || '',
        securityAnswer2: userData.securityAnswer2 || '',
        securityQuestion3: userData.securityQuestion3 || '',
        securityAnswer3: userData.securityAnswer3 || ''
      };

      // Don't auto-login after signup - user needs to sign in to get a valid token
      // setUser(newUser);
      // localStorage.setItem('user', JSON.stringify(newUser));
      return { success: true };
    } catch (error) {
      console.error('Signup error:', error);
      return { success: false, error: error.message || 'Signup failed' };
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (updatedUserData) => {
    setIsLoading(true);
    try {
      // Prepare the payload with only fields the backend supports
      // Database fields: first_name, last_name, phone_number, email, profile_image
      const apiPayload = {};

      // Handle name - can come as full name or firstName/lastName
      if (updatedUserData.firstName) {
        apiPayload.firstName = updatedUserData.firstName;
      } else if (updatedUserData.name) {
        apiPayload.firstName = updatedUserData.name.split(' ')[0];
      }

      if (updatedUserData.lastName) {
        apiPayload.lastName = updatedUserData.lastName;
      } else if (updatedUserData.name) {
        apiPayload.lastName = updatedUserData.name.split(' ').slice(1).join(' ') || '';
      }

      if (updatedUserData.phone || updatedUserData.phoneNumber) {
        apiPayload.phoneNumber = updatedUserData.phone || updatedUserData.phoneNumber;
      }

      if (updatedUserData.email) {
        apiPayload.email = updatedUserData.email;
      }

      if (updatedUserData.dob !== undefined) {
        apiPayload.dob = updatedUserData.dob;
      }

      if (updatedUserData.gender !== undefined) {
        apiPayload.gender = updatedUserData.gender;
      }

      if (updatedUserData.userHandle !== undefined) {
        apiPayload.userHandle = updatedUserData.userHandle;
      }


      // Call the actual API to update user
      const response = await profileAPI.update(user.id, apiPayload);

      // Fetch fresh user data from API to ensure we have the latest
      const freshUserData = await profileAPI.getUserById(user.id);

      // Build updated user object from API response
      const updatedUser = {
        ...user,
        id: freshUserData.id,
        name: `${freshUserData.firstName || freshUserData.first_name} ${freshUserData.lastName || freshUserData.last_name}`.trim(),
        firstName: freshUserData.firstName || freshUserData.first_name,
        lastName: freshUserData.lastName || freshUserData.last_name,
        email: freshUserData.email,
        phoneNumber: freshUserData.phoneNumber || freshUserData.phone_number,
        phone: freshUserData.phoneNumber || freshUserData.phone_number,
        profileImage: freshUserData.profileImage || null,
        dob: freshUserData.dob || null,
        gender: freshUserData.gender || null,
        userHandle: freshUserData.userHandle || null,
      };

      setUser(updatedUser);
      // Note: User data is kept in memory only, not in localStorage
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message || 'Profile update failed' };
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth on app load - fetch fresh user data from API using token
  React.useEffect(() => {
    const initAuth = async () => {
      const token = await secureStorage.getItem('token');
      const socialUserStr = await secureStorage.getItem('socialUser');

      if (token && socialUserStr) {
        try {
          // Parse stored social user to get user ID
          const socialUser = JSON.parse(socialUserStr);

          // Fetch fresh user data from API
          const freshUserData = await profileAPI.getUserById(socialUser.id);

          // Build user object from fresh API data
          const userData = {
            id: freshUserData.id,
            name: `${freshUserData.firstName || freshUserData.first_name} ${freshUserData.lastName || freshUserData.last_name}`.trim(),
            firstName: freshUserData.firstName || freshUserData.first_name,
            lastName: freshUserData.lastName || freshUserData.last_name,
            email: freshUserData.email,
            phoneNumber: freshUserData.phoneNumber || freshUserData.phone_number,
            phone: freshUserData.phoneNumber || freshUserData.phone_number,
            profileImage: freshUserData.profileImage || null,
            dob: freshUserData.dob || null,
            gender: freshUserData.gender || null,
            userHandle: freshUserData.userHandle || null,
            hasPassword: freshUserData.hasPassword ?? false,
            avatar: null,
            sport: 'Basketball',
            rating: 4.8,
            wins: 47,
            losses: 12,
            friends: 234,
            teammates: 18,
            totalPoints: 1247,
            rank: '#12 in City',
            // KYN-499: resolved feature entitlements from GET /user/:id (session context).
            // Fail closed on a missing/old response so gated features stay hidden.
            featureFlags: freshUserData.featureFlags || {},
          };

          setUser(userData);
          setSessionCookie();
        } catch (err) {
          console.error('Failed to load user data from API:', err);
          // Clear session on 401 (invalid token) or 404 (user no longer exists in DB,
          // e.g. after a UUID migration where IDs were regenerated).
          // Network errors or 5xx (server restarting) should NOT log the user out.
          if (err?.status === 401 || err?.status === 404) {
            clearSessionCookie();
            secureStorage.removeItem('token');
            secureStorage.removeItem('socialUser');
            setUser(null);
          } else {
            // Server temporarily unavailable — keep token, restore user from storage
            const socialUser = await secureStorage.getItem('socialUser');
            if (socialUser) {
              try {
                const parsed = JSON.parse(socialUser);
                setUser({
                  id: parsed.id,
                  name: `${parsed.firstName} ${parsed.lastName}`.trim(),
                  firstName: parsed.firstName,
                  lastName: parsed.lastName,
                  email: parsed.email,
                  avatar: null,
                  sport: 'Basketball',
                  rating: 4.8, wins: 47, losses: 12,
                  friends: 234, teammates: 18, totalPoints: 1247, rank: '#12 in City',
                });
                setSessionCookie();
              } catch (_) {
                setUser(null);
              }
            }
          }
        }
      } else {
        // No token - ensure clean state
        setUser(null);
      }

      setAuthChecked(true);
    };

    initAuth();
  }, []);

  const updateUserField = (updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : prev);
  };

  const refreshProfile = async () => {
    if (!user?.id) {
      console.warn('No user to refresh');
      return { success: false, error: 'No user logged in' };
    }

    setIsLoading(true);
    try {
      // Fetch fresh user data from API
      const freshUserData = await profileAPI.getUserById(user.id);

      // Build updated user object
      const updatedUser = {
        ...user,
        id: freshUserData.id,
        name: `${freshUserData.firstName || freshUserData.first_name} ${freshUserData.lastName || freshUserData.last_name}`.trim(),
        firstName: freshUserData.firstName || freshUserData.first_name,
        lastName: freshUserData.lastName || freshUserData.last_name,
        email: freshUserData.email,
        phoneNumber: freshUserData.phoneNumber || freshUserData.phone_number,
        phone: freshUserData.phoneNumber || freshUserData.phone_number,
        profileImage: freshUserData.profileImage || null,
        dob: freshUserData.dob || null,
        gender: freshUserData.gender || null,
        userHandle: freshUserData.userHandle || null,
      };

      setUser(updatedUser);
      // Note: User data is kept in memory only, not in localStorage
      return { success: true };
    } catch (error) {
      console.error('Profile refresh error:', error);
      return { success: false, error: error.message || 'Failed to refresh profile' };
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    authChecked,
    sessionExpired,
    sessionWarning,
    login,
    googleLogin,
    facebookLogin,
    appleLogin,
    logout,
    signup,
    updateProfile,
    refreshProfile,
    updateUserField,
    clearSessionExpired,
    extendSession,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};